import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { ChatMode, IChatConfig, JevaMode } from "../../types/chatConfig";
import { AxiosResponse } from "axios";
import { ICampaign } from "../../types/campaign";
import { biApi } from "../../utils/api";
import { GET_CAMPAIGN_TOKEN, GET_CHAT_STATUS } from "../../utils/constants";

export type CampaignDetails = Omit<ICampaign, "globalChatConfig">;
export interface IChatConfigPayload {
  chatConfig: IChatConfig;
  campaignDetails: CampaignDetails;
}

export interface IChatConfigState {
  loading: boolean;
  chatConfig: IChatConfig | null;
  activeChat: boolean;
  activityId: number | null;
  error: string;
  campaignDetails: CampaignDetails | null;
  chatMode: ChatMode;
  userQuestion: string;
  jevaMode: JevaMode;
}

export interface IChatStatus {
  id: number;
  active: boolean;
}

const initialState: IChatConfigState = {
  loading: true,
  chatConfig: null,
  activeChat: false,
  activityId: null,
  campaignDetails: null,
  error: "",
  chatMode: "TEXT",
  jevaMode: "",
  userQuestion: "",
};

export const loadChatConfig = createAsyncThunk("loadChatConfig", async (campaignId: string): Promise<IChatConfigPayload> => {
  const response: AxiosResponse<ICampaign> = await biApi.post(
    "process-api/chat/get-campaign",
    { id: campaignId },
    {
      headers: {
        "x-api-endpoint": GET_CAMPAIGN_TOKEN,
      },
    }
  );
  const { globalChatConfig, ...campaignDetails } = response.data;
  return {
    campaignDetails,
    chatConfig: JSON.parse(JSON.parse(globalChatConfig.replace(/#/g, "")).globalChatConfig),
  };
});

export const loadChatStatus = createAsyncThunk("loadChatStatus", async (activityId: string): Promise<IChatStatus> => {
  const response: AxiosResponse = await biApi.post(
    "process-api/chat/status-check",
    { activityId: activityId },
    {
      headers: {
        "x-api-endpoint": GET_CHAT_STATUS,
      },
    }
  );

  return response.data.data;
});

const chatConfigSlice = createSlice({
  name: "chatConfig",
  initialState,
  reducers: {
    setUserQuestion: (state, { payload }: PayloadAction<string>) => {
      state.userQuestion = payload;
    },
    updateJevaMode: (state, { payload }: PayloadAction<JevaMode>) => {
      state.jevaMode = payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(loadChatConfig.pending, (state, action) => {
        state.loading = true;
        state.campaignDetails = null;
        state.chatConfig = null;
        state.error = "";
        state.chatMode = "TEXT";
      })
      .addCase(loadChatConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.campaignDetails = action.payload.campaignDetails;
        state.chatConfig = action.payload.chatConfig;
        state.error = "";
        const { isTextEnabled, isVoiceEnabled } = action.payload.chatConfig.config.queue1;
        if (isTextEnabled && isVoiceEnabled) {
          state.chatMode = "BOTH";
        } else if (isVoiceEnabled) {
          state.chatMode = "VOICE";
        } else {
          state.chatMode = "TEXT";
        }
      })
      .addCase(loadChatConfig.rejected, (state, action) => {
        state.loading = false;
        state.campaignDetails = null;
        state.chatConfig = null;
        state.error = action.error.message || "";
        state.chatMode = "TEXT";
      })
      .addCase(loadChatStatus.fulfilled, (state, { payload }) => {
        const chatStatus = payload;
        state.activeChat = chatStatus.active;
        if (chatStatus.active) {
          state.activityId = chatStatus.id;
        }
      });
  },
});

export const { setUserQuestion, updateJevaMode } = chatConfigSlice.actions;

export default chatConfigSlice.reducer;
